import React, {FC, useContext} from 'react';
import {ModerationUser} from "interfaces";
import {ModerationPic} from "./ModerationPic";
import {hasAIPhotos, isDeleteAllAvailable, ReviewTagInfoTypes, UserReviewTags, UserStatuses} from "enums";
import {ModerationPhotoProvider} from "components/ModerationUserCard/ModerationPhotos/PhotoProvider";
import {FaRegTrashAlt} from "react-icons/fa";
import {usePhotoManagement} from "components/ModerationUserCard/ModerationPhotos/usePhotoManagement";
import {AuthContext} from "contexts";
import {Events, eventTracker, ModerationSources} from "utils/eventTracker";
import {useSearchParams} from "react-router-dom";

const PHOTO_SEARCH_PARAM = 'photo';


interface Props {
    user: ModerationUser
    onUserChange: (user: ModerationUser) => void;
    onReload?: () => void;
    syncPhotoWithUrl?: boolean;
}

export const ModerationPhotos: FC<Props> = ({user, onUserChange, onReload, syncPhotoWithUrl}) => {
    const {env} = useContext(AuthContext);
    const photos = user.photos ?? [];
    const photoManager = usePhotoManagement()
    const [searchParams, setSearchParams] = useSearchParams();

    const onOpenedPhotoChange = (key: string | null) => {
        const params = new URLSearchParams(searchParams);
        if (key) {
            params.set(PHOTO_SEARCH_PARAM, key);
        } else {
            params.delete(PHOTO_SEARCH_PARAM);
        }
        setSearchParams(params, {replace: true});
    }

    if (!photos.length) {
        return <div className="col-12 col-md-6">Нет фото</div>
    }

    const onPhotoDeleted = (key: string) => {
        const photo = user.photos?.find(p => p.key === key);
        if (photo) {
            const eventParams = eventTracker.getReviewParams(user, null, ModerationSources.Moderation);
            eventTracker.track(Events.photo_delete, eventTracker.getPhotoParams(photo, eventParams))
        }
        onUserChange({...user, photos: photos.filter(p => p.key !== key)})
    }

    const onSensitiveTagChanged = (key: string, isSensitive: boolean) => {
        const photo = user.photos?.find(p => p.key === key);
        if (photo) {
            const eventParams = eventTracker.getReviewParams(user, null, ModerationSources.Moderation);
            eventTracker.track(Events.photo_update, eventTracker.getPhotoParams(photo, {
                ...eventParams,
                type: isSensitive ? 'added_Sensitive' : 'removed_Sensitive'
            }))
        }
        onUserChange({...user, photos: photos.map(p => p.key === key ? {...p, isSensitive} : p)})
    }

    const onNoFaceChanged = (key: string, newHasNoFace: boolean) => {
        const photo = user.photos?.find(p => p.key === key);
        if (photo) {
            const eventParams = eventTracker.getReviewParams(user, null, ModerationSources.Moderation);
            eventTracker.track(Events.photo_update, eventTracker.getPhotoParams(photo, {
                ...eventParams,
                type: newHasNoFace ? 'added_NoFace' : 'removed_NoFace'
            }))
        }
        onUserChange({
            ...user,
            reviewTags: newHasNoFace ?
                [...user.reviewTags, {tag: UserReviewTags.NoFace, infoValue: key, infoType: ReviewTagInfoTypes.Photo}]
                : user.reviewTags.filter(t => !(t.tag === UserReviewTags.NoFace && t.infoValue === key))
        })
    }

    const onAITagChanged = (key: string, isOn: boolean) => {
        const photo = user.photos?.find(p => p.key === key);
        if (photo) {
            const eventParams = eventTracker.getReviewParams(user, null, ModerationSources.Moderation);
            eventTracker.track(Events.photo_update, eventTracker.getPhotoParams(photo, {
                ...eventParams,
                type: isOn ? 'added_AI' : 'removed_AI'
            }))
        }
        if (onReload) {
            onReload();
        } else {
            onUserChange({...user, photos: photos.map(p => p.key === key ? {...p, isAiGenerated: isOn} : p)})
        }
    }

    return <>
        {user.photos?.length && isDeleteAllAvailable(env) && user.status !== UserStatuses.BlackList &&
            <div>
                <button type="button"
                        className="btn btn-outline-secondary btn-sm d-flex flex-row align-items-center"
                        onClick={async () => {
                            const result = await photoManager.onDeleteAllPics(user.id, user.photos?.map(p => p.key))
                            const eventParams = eventTracker.getReviewParams(user, null, ModerationSources.Moderation);
                            eventTracker.track(Events.profile_open, eventParams)
                            if (user.photos) {
                                for (let i = 0; i < user.photos.length; i++) {
                                    eventTracker.track(Events.photo_delete, eventTracker.getPhotoParams(user.photos[i], eventParams))
                                }
                            }
                            if (result) {
                                onUserChange({
                                    ...user,
                                    photos: [],
                                })
                            }
                        }}>
                    <FaRegTrashAlt size={14} style={{marginRight: 4}}/>
                    Удалить все фото
                </button>
            </div>
        }
        <ModerationPhotoProvider
            userId={user.id}
            user={user}
            photos={photos}
            onPhotoDeleted={onPhotoDeleted}
            onSensitiveTagChanged={onSensitiveTagChanged}
            openedPhotoKey={syncPhotoWithUrl ? searchParams.get(PHOTO_SEARCH_PARAM) : null}
            onOpenedPhotoChange={syncPhotoWithUrl ? onOpenedPhotoChange : undefined}>
            {photos.map(p => {
                if (!p || !p.image || !p.image.length) {
                    return null
                }
                return <div key={p.image[0].link} className="col-6 col-xl-6 align-items-stretch px-1 py-1">
                    <ModerationPic
                        user={user}
                        pic={p}
                        onSensitiveTagChanged={onSensitiveTagChanged}
                        onPhotoDeleted={onPhotoDeleted}
                        onNoFaceChanged={onNoFaceChanged}
                        onAITagChanged={hasAIPhotos(env) ? onAITagChanged : undefined}
                    />
                </div>
            })}
        </ModerationPhotoProvider>
    </>
}

